/**
 * ThreeSixtyPreviewModal — 360° LED Ribbon Preview
 *
 * Renders 360 content across venue levels (FOP, L2, L3, L4) as flat planes
 * in stack or strip layout with chunked sequential playback per level.
 * Supports any venue prefix (MCG, Marvel, SCG, etc.) and both video and
 * still-image assets. Adapted from ribbon-v6b.html.
 *
 * Props:
 *   asset          – the parent asset record (for title display)
 *   ribbonItems    – array of { name, downloadUrl } for each clip
 *   onClose        – callback to close the modal
 */

import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

// ── Colour constants (self-contained, no theme dependency) ───────────
const TEAL = "#2fd6b5";
const TEXT_SECONDARY = "rgba(255,255,255,0.5)";

// ── Helpers ──────────────────────────────────────────────────────────────
function isVideo(name) {
  return /\.(mp4|webm|mov|m4v)$/i.test(name || "");
}

function directUrl(url) {
  if (!url) return "";
  return url
    .replace(/^_+/, "").replace(/_+$/, "")
    .replace("dl=0", "dl=1")
    .replace("www.dropbox.com", "dl.dropboxusercontent.com");
}

function fmt(s) {
  if (!isFinite(s) || s < 0) s = 0;
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
}

/**
 * Extract the full region tag from a filename.
 * Matches any venue prefix before the level: MCG-FOP-1, Marvel-L2-3, SCG_L4_2, etc.
 * Handles both hyphen and underscore separators.
 */
function parseRegion(name) {
  const m = (name || "").match(/([A-Za-z0-9]+)[-_](FOP|L2|L3|L4)[-_](\d+)/i);
  return m ? `${m[1].toUpperCase()}-${m[2].toUpperCase()}-${m[3]}` : "";
}

/**
 * Extract just the level tag (FOP, L2, L3, L4) from a filename.
 * Works for any venue prefix — looks for [-_]LEVEL[-_] anywhere in the name.
 */
function parseLevel(name) {
  const m = (name || "").match(/[-_](FOP|L2|L3|L4)[-_]/i);
  return m ? m[1].toUpperCase() : null;
}

function parseIndex(name) {
  const m = (name || "").match(/[-_](\d+)\.\w+$/);
  return m ? parseInt(m[1]) : 0;
}

const LEVEL_COLORS = { FOP: "#58a6ff", L2: "#bc8cff", L3: "#f778ba", L4: "#ffa657" };
const STRIP_W = 40;
const STACK_W = 12;
const MAX_TEX = 4;
const IMAGE_HOLD_SECS = 10; // seconds to display an image-only chunk before auto-advancing

// ── Component ────────────────────────────────────────────────────────────
export default function ThreeSixtyPreviewModal({ asset, ribbonItems, onClose }) {
  const wrapperRef = useRef(null);    // outer modal — for querying all data-id elements
  const containerRef = useRef(null);  // viewport div — for Three.js canvas
  const stateRef = useRef(null);

  // ── Escape key ──
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", h);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  // ── Three.js init ──
  useEffect(() => {
    if (!containerRef.current || !wrapperRef.current || !ribbonItems || ribbonItems.length === 0) return;

    let cancelled = false;
    let cleanupFn = null;

    (async () => {
      const THREE = await import(
        /* webpackIgnore: true */
        "https://cdn.jsdelivr.net/npm/three@0.160.0/build/three.module.js"
      );

      if (cancelled) return;

      // ── Organise files by level ──
      const levelFiles = {};
      ribbonItems.forEach((f) => {
        const l = parseLevel(f.name);
        if (!l) return;
        if (!levelFiles[l]) levelFiles[l] = [];
        levelFiles[l].push({ ...f, index: parseIndex(f.name), region: parseRegion(f.name) });
      });
      Object.values(levelFiles).forEach((a) => a.sort((x, y) => x.index - y.index));
      const LEVELS = ["FOP", "L2", "L3", "L4"].filter((l) => levelFiles[l]?.length > 0);

      if (LEVELS.length === 0) { onClose(); return; }

      // ── Renderer ──
      const el = containerRef.current;
      const renderer = new THREE.WebGLRenderer({ antialias: true });
      renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      el.insertBefore(renderer.domElement, el.firstChild);

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0x0b0e14);
      const cam = new THREE.PerspectiveCamera(50, 1, 0.01, 200);
      cam.position.set(0, 0, 10);
      const camTarget = new THREE.Vector3(0, 0, 0);
      cam.lookAt(camTarget);
      scene.add(new THREE.AmbientLight(0xffffff, 0.3));

      // ── Manual pan / zoom controls ──
      let isDragging = false, lastPtrX = 0, lastPtrY = 0;
      const PAN_SPEED = 0.003;
      const ZOOM_MIN = 0.5, ZOOM_MAX = 100;

      function onPointerDown(e) {
        isDragging = true; lastPtrX = e.clientX; lastPtrY = e.clientY;
        renderer.domElement.style.cursor = "grabbing";
      }
      function onPointerMove(e) {
        if (!isDragging) return;
        const dx = e.clientX - lastPtrX, dy = e.clientY - lastPtrY;
        lastPtrX = e.clientX; lastPtrY = e.clientY;
        const dist = cam.position.distanceTo(camTarget);
        const panScale = dist * PAN_SPEED;
        camTarget.x -= dx * panScale; camTarget.y += dy * panScale;
        cam.position.x -= dx * panScale; cam.position.y += dy * panScale;
      }
      function onPointerUp() { isDragging = false; renderer.domElement.style.cursor = "grab"; }
      function onWheel(e) {
        e.preventDefault();
        const dir = new THREE.Vector3().subVectors(camTarget, cam.position).normalize();
        const zoomAmt = e.deltaY * 0.002 * cam.position.distanceTo(camTarget);
        const newPos = cam.position.clone().addScaledVector(dir, zoomAmt);
        if (newPos.distanceTo(camTarget) >= ZOOM_MIN && newPos.distanceTo(camTarget) <= ZOOM_MAX) {
          cam.position.copy(newPos);
        }
      }
      // Touch pan
      function onTouchStart(e) {
        if (e.touches.length === 1) { isDragging = true; lastPtrX = e.touches[0].clientX; lastPtrY = e.touches[0].clientY; }
      }
      function onTouchMove(e) {
        if (!isDragging || e.touches.length !== 1) return;
        const dx = e.touches[0].clientX - lastPtrX, dy = e.touches[0].clientY - lastPtrY;
        lastPtrX = e.touches[0].clientX; lastPtrY = e.touches[0].clientY;
        const dist = cam.position.distanceTo(camTarget);
        const panScale = dist * PAN_SPEED;
        camTarget.x -= dx * panScale; camTarget.y += dy * panScale;
        cam.position.x -= dx * panScale; cam.position.y += dy * panScale;
      }
      function onTouchEnd() { isDragging = false; }

      renderer.domElement.style.cursor = "grab";
      renderer.domElement.addEventListener("pointerdown", onPointerDown);
      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
      renderer.domElement.addEventListener("wheel", onWheel, { passive: false });
      renderer.domElement.addEventListener("touchstart", onTouchStart, { passive: true });
      renderer.domElement.addEventListener("touchmove", onTouchMove, { passive: true });
      renderer.domElement.addEventListener("touchend", onTouchEnd);

      function resize() {
        if (!el) return;
        const w = el.clientWidth, h = el.clientHeight;
        if (w === 0 || h === 0) return;
        cam.aspect = w / h; cam.updateProjectionMatrix();
        renderer.setSize(w, h);
        // Re-fit camera framing to current content on resize
        if (tiles.length) fit();
      }

      // Style canvas to fill container
      renderer.domElement.style.display = "block";
      renderer.domElement.style.width = "100%";
      renderer.domElement.style.height = "100%";

      // ── State ──
      let mode = "stack", level = null, tiles = [], meshes = [], grp = new THREE.Group();
      scene.add(grp);
      let on = false, cs0 = 0, adv = false, chunk = [], sel = null, waiting = false;
      let imageTimerStart = null; // timestamp when image-only chunk started displaying

      // Now safe to call resize (tiles is declared above)
      resize();
      window.addEventListener("resize", resize);

      // ResizeObserver for robust container-level resize detection
      let ro = null;
      if (typeof ResizeObserver !== "undefined") {
        ro = new ResizeObserver(() => resize());
        ro.observe(el);
      }

      function CS() { return tiles.length >= 7 ? 4 : 3; }

      // ── DOM refs (query from wrapper, not viewport, so buttons/infobar are found) ──
      const wrapper = wrapperRef.current;
      const getEl = (id) => wrapper ? wrapper.querySelector(`[data-id="${id}"]`) : null;

      // ── Load tile ──
      function loadTile(f) {
        return new Promise((res) => {
          if (!isVideo(f.name)) {
            const i = new Image(); i.crossOrigin = "anonymous";
            i.onload = () => {
              const t = new THREE.Texture(i);
              t.colorSpace = THREE.SRGBColorSpace; t.minFilter = THREE.LinearFilter;
              t.magFilter = THREE.LinearFilter; t.generateMipmaps = false; t.needsUpdate = true;
              res({ ty: "i", tex: t, vid: null, f, w: i.naturalWidth, h: i.naturalHeight, reg: f.region, nf: false });
            };
            i.onerror = () => res(errT(f));
            i.src = directUrl(f.downloadUrl);
            return;
          }
          const v = document.createElement("video");
          v.crossOrigin = "anonymous"; v.loop = false; v.muted = true;
          v.playsInline = true; v.preload = "auto";
          const to = setTimeout(() => { v.src = ""; v.src = directUrl(f.downloadUrl); v.load(); }, 15000);
          v.addEventListener("loadedmetadata", () => {
            clearTimeout(to);
            const tx = new THREE.VideoTexture(v);
            tx.colorSpace = THREE.SRGBColorSpace; tx.minFilter = THREE.LinearFilter;
            tx.magFilter = THREE.LinearFilter; tx.generateMipmaps = false;
            const t = { ty: "v", tex: tx, vid: v, f, w: v.videoWidth, h: v.videoHeight, reg: f.region, nf: false };
            function onF() { t.nf = true; if (!v.paused) v.requestVideoFrameCallback(onF); }
            t._r = () => { if ("requestVideoFrameCallback" in v) v.requestVideoFrameCallback(onF); else t.nf = true; };
            res(t);
          }, { once: true });
          v.addEventListener("error", () => { clearTimeout(to); res(errT(f)); }, { once: true });
          v.src = directUrl(f.downloadUrl); v.load();
        });
      }

      function errT(f) {
        const c = document.createElement("canvas"); c.width = 960; c.height = 48;
        return { ty: "i", tex: new THREE.CanvasTexture(c), vid: null, f, w: 960, h: 48, reg: f.region, nf: false };
      }

      function unload() {
        tiles.forEach((t) => {
          if (t.vid) { t.vid.pause(); t.vid.removeAttribute("src"); t.vid.load(); }
          if (t.tex) t.tex.dispose();
        });
        while (grp.children.length) {
          const c = grp.children[0]; grp.remove(c);
          if (c.material?.map) c.material.map.dispose();
          if (c.material) c.material.dispose();
          if (c.geometry) c.geometry.dispose();
        }
        tiles = []; meshes = []; chunk = []; sel = null;
      }

      async function load(lv) {
        const files = levelFiles[lv];
        const sub = getEl("ov-sub");
        const spinner = getEl("ov-title");
        const ov = getEl("overlay");
        if (ov) ov.style.opacity = "1";
        if (spinner) { spinner.innerHTML = ""; spinner.style.color = LEVEL_COLORS[lv]; }
        if (sub) sub.textContent = `Loading ${lv} — 0 / ${files.length}`;

        const arr = [];
        for (let i = 0; i < files.length; i += 2) {
          const b = files.slice(i, i + 2).map((f) => loadTile(f));
          arr.push(...(await Promise.all(b)));
          if (sub) sub.textContent = `Loading ${lv} — ${arr.length} / ${files.length}`;
        }
        tiles = arr; level = lv;
        const fv = tiles.find((t) => t.vid);
        if (fv) { const dur = getEl("t-dur"); if (dur) dur.textContent = fmt(fv.vid.duration); }
        if (ov) ov.style.opacity = "0";
      }

      // ── Build ──
      function build() {
        while (grp.children.length) {
          const c = grp.children[0]; grp.remove(c);
          if (c.geometry) c.geometry.dispose(); if (c.material) c.material.dispose();
        }
        meshes = [];
        if (!tiles.length) return;
        if (mode === "strip") bStrip(); else bStack();
      }

      function bStrip() {
        const tpx = tiles.reduce((s, t) => s + t.w, 0), sc = STRIP_W / tpx;
        let x = -(tpx * sc) / 2;
        tiles.forEach((t) => {
          const tw = t.w * sc, th = t.h * sc; t.ww = tw; t.wh = th;
          const g = new THREE.PlaneGeometry(tw, th);
          const m = new THREE.MeshBasicMaterial({ map: t.tex, side: THREE.DoubleSide, toneMapped: false, transparent: true });
          const mesh = new THREE.Mesh(g, m); mesh.position.set(x + tw / 2, 0, 0);
          mesh.userData = { t }; grp.add(mesh); meshes.push(mesh); t.mesh = mesh; x += tw;
        });
      }

      function bStack() {
        tiles.forEach((t) => { t.sh = (t.h / t.w) * STACK_W; });
        const gap = 0.1;
        const totH = tiles.reduce((s, t) => s + t.sh, 0) + (tiles.length - 1) * gap;
        let y = totH / 2;
        tiles.forEach((t) => {
          y -= t.sh / 2; t.ww = STACK_W; t.wh = t.sh;
          const g = new THREE.PlaneGeometry(STACK_W, t.sh);
          const m = new THREE.MeshBasicMaterial({ map: t.tex, side: THREE.DoubleSide, toneMapped: false, transparent: true });
          const mesh = new THREE.Mesh(g, m); mesh.position.set(0, y, 0);
          mesh.userData = { t }; grp.add(mesh); meshes.push(mesh); t.mesh = mesh;
          y -= t.sh / 2 + gap;
        });
      }

      function fit() {
        if (!tiles.length) return;
        let w, h;
        if (mode === "strip") {
          w = tiles.reduce((s, t) => s + (t.ww || 0), 0);
          h = tiles[0]?.wh || 1;
        } else {
          w = STACK_W;
          h = tiles.reduce((s, t) => s + (t.sh || 0), 0) + tiles.length * 0.1;
        }
        const fv = cam.fov * Math.PI / 180, a = cam.aspect;
        const d = Math.max((w / 2) / (Math.tan(fv / 2) * a), (h / 2) / Math.tan(fv / 2)) * 1.15;
        cam.position.set(0, 0, d); camTarget.set(0, 0, 0); cam.lookAt(camTarget);
      }

      // ── Playback ──
      function waitR(vs) {
        return new Promise((r) => {
          let d = false;
          function ck() { if (d) return; if (vs.every((v) => v.readyState >= 3)) { d = true; r(); } }
          vs.forEach((v) => v.addEventListener("canplay", ck));
          const iv = setInterval(() => { ck(); if (d) clearInterval(iv); }, 100);
          setTimeout(() => { if (!d) { d = true; r(); } }, 10000);
        });
      }

      function sync() {
        if (chunk.length < 2) return;
        const ref = chunk.find((t) => t.vid && !t.vid.paused && t.vid.readyState >= 2);
        if (!ref) return;
        chunk.forEach((t) => {
          if (!t.vid || t.vid.paused || t.vid.readyState < 2 || t === ref) return;
          const d = Math.abs(t.vid.currentTime - ref.vid.currentTime);
          if (d > 0.2) { t.vid.currentTime = ref.vid.currentTime; t.vid.playbackRate = 1; }
          else if (d > 0.06) { t.vid.playbackRate = t.vid.currentTime > ref.vid.currentTime ? 0.95 : 1.05; }
          else t.vid.playbackRate = 1;
        });
      }

      function done() {
        const vs = chunk.filter((t) => t.vid);
        if (vs.length > 0) {
          // Has videos — done when all videos have ended
          return vs.every((t) => t.vid.ended || (t.vid.duration > 0 && t.vid.currentTime >= t.vid.duration - 0.15));
        }
        // Image-only chunk — done after IMAGE_HOLD_SECS
        if (imageTimerStart && chunk.length > 0) {
          return (performance.now() - imageTimerStart) / 1000 >= IMAGE_HOLD_SECS;
        }
        return false;
      }

      async function playChunk() {
        if (!on || !level) return;
        imageTimerStart = null;
        const cs = CS(), end = Math.min(cs0 + cs, tiles.length);
        chunk = tiles.slice(cs0, end);
        tiles.forEach((t) => { if (t.vid) { t.vid.pause(); t.vid.playbackRate = 1; } });
        tiles.forEach((t) => { if (t.mesh) t.mesh.material.opacity = chunk.includes(t) ? 1 : 0.1; });
        // Unload non-chunk videos
        tiles.forEach((t) => {
          if (!t.vid || chunk.includes(t)) return;
          if (!t._b) t._b = t.vid.src;
          if (t.vid.src) { t.vid.removeAttribute("src"); t.vid.load(); t._u = true; }
        });
        const rl = [];
        chunk.forEach((t) => {
          if (t._u && t._b) {
            t.vid.src = t._b; t.vid.load(); t._u = false;
            rl.push(new Promise((r) => { t.vid.addEventListener("loadeddata", r, { once: true }); setTimeout(r, 8000); }));
          }
        });
        if (rl.length) await Promise.all(rl);
        if (!on) return;
        const vs = chunk.filter((t) => t.vid).map((t) => t.vid);
        const isImageOnly = vs.length === 0;
        if (vs.length) await waitR(vs);
        if (!on) return;
        const dur = isImageOnly ? IMAGE_HOLD_SECS : (vs.length > 0 ? Math.min(...vs.map((v) => v.duration)) : 30);
        const tDur = getEl("t-dur"); if (tDur) tDur.textContent = fmt(dur);
        const tCur = getEl("t-cur"); if (tCur) tCur.textContent = "0:00";
        if (isImageOnly) {
          // Start hold timer for image-only chunks
          imageTimerStart = performance.now();
        } else {
          chunk.forEach((t) => { if (t.vid) { t.vid.currentTime = 0; t.vid.play().catch(() => {}); t._r?.(); } });
        }
        ui();
      }

      function nextLevelName() {
        const ci = LEVELS.indexOf(level), ni = (ci + 1) % LEVELS.length;
        return LEVELS[ni];
      }

      async function advance() {
        if (adv || !on || waiting) return;
        adv = true;
        const cs = CS(), next = cs0 + cs;
        if (next < tiles.length) {
          cs0 = next; await playChunk();
        } else {
          waiting = true;
          const nl = nextLevelName();
          const ldEl = getEl("level-done"); if (ldEl) ldEl.style.display = "";
          const ldText = getEl("ld-text");
          if (ldText) { ldText.textContent = `${level} complete`; ldText.style.color = LEVEL_COLORS[level] || "#8b949e"; }
          const contBar = getEl("continue-bar"); if (contBar) contBar.style.display = "";
          const contBtn = getEl("btn-continue");
          if (contBtn) {
            contBtn.textContent = `Continue to ${nl} →`;
            contBtn.style.borderColor = LEVEL_COLORS[nl];
            contBtn.style.color = LEVEL_COLORS[nl];
          }
          const ip = getEl("ib-progress"); if (ip) ip.textContent = `${level} done · ${nl} next`;
          const chunkDur = chunk.some((t) => t.vid) ? (chunk[0]?.vid?.duration || IMAGE_HOLD_SECS) : IMAGE_HOLD_SECS;
          const tCur = getEl("t-cur"); if (tCur) tCur.textContent = fmt(chunkDur);
        }
        adv = false;
      }

      async function continueToNext() {
        if (!waiting || !on) return;
        waiting = false;
        const contBar = getEl("continue-bar"); if (contBar) contBar.style.display = "none";
        const ldEl = getEl("level-done"); if (ldEl) ldEl.style.display = "none";
        const nl = nextLevelName();
        unload(); await load(nl); build(); fit(); cs0 = 0; await playChunk();
      }

      async function go(lv) {
        on = true; waiting = false;
        const contBar = getEl("continue-bar"); if (contBar) contBar.style.display = "none";
        const ldEl = getEl("level-done"); if (ldEl) ldEl.style.display = "none";
        unload(); await load(lv); build(); fit(); cs0 = 0; await playChunk();
        ui();
      }

      function stop() {
        on = false; adv = false; waiting = false; imageTimerStart = null;
        tiles.forEach((t) => { if (t.vid) { t.vid.pause(); t.vid.currentTime = 0; t.vid.playbackRate = 1; } });
        chunk = [];
        const tCur = getEl("t-cur"); if (tCur) tCur.textContent = "0:00";
        const contBar = getEl("continue-bar"); if (contBar) contBar.style.display = "none";
        const ldEl = getEl("level-done"); if (ldEl) ldEl.style.display = "none";
        ui();
      }

      // ── Info bar update ──
      function ui() {
        // Level buttons
        LEVELS.forEach((l) => {
          const b = getEl(`btn-${l.toLowerCase()}`);
          if (b) {
            const isOn = l === level;
            b.style.color = isOn ? (LEVEL_COLORS[l] || "#ccc") : "";
            b.style.borderColor = isOn ? (LEVEL_COLORS[l] || "#ccc") : "";
            b.style.background = isOn ? `${LEVEL_COLORS[l]}18` : "";
          }
        });
        // Mode buttons
        const bStrip = getEl("btn-strip"), bStack = getEl("btn-stack");
        if (bStrip) { bStrip.style.color = mode === "strip" ? "#bc8cff" : ""; bStrip.style.borderColor = mode === "strip" ? "rgba(188,140,255,0.4)" : ""; }
        if (bStack) { bStack.style.color = mode === "stack" ? "#bc8cff" : ""; bStack.style.borderColor = mode === "stack" ? "rgba(188,140,255,0.4)" : ""; }
        // Info bar
        const ib = getEl("ib-level"), ir = getEl("ib-regions"), ip = getEl("ib-progress");
        if (level && tiles.length > 0) {
          if (ib) { ib.textContent = level; ib.style.color = LEVEL_COLORS[level] || "#ccc"; }
          const regions = chunk.map((t) => t.reg).filter(Boolean);
          if (ir) ir.textContent = regions.length > 0 ? regions.join(" , ") : "—";
          const cs = CS(), end = Math.min(cs0 + cs, tiles.length);
          if (ip) ip.textContent = `Playing ${cs0 + 1}–${end} of ${tiles.length} clips (${cs} at a time)`;
        } else {
          if (ib) ib.textContent = "";
          if (ir) ir.textContent = "—";
          if (ip) ip.textContent = "";
        }
        // Overlay
        const ov = getEl("overlay");
        if (!level && !on) {
          if (ov) ov.style.opacity = "1";
          const sub = getEl("ov-sub");
          if (sub) sub.textContent = `Press Play to start from ${LEVELS[0] || "FOP"}`;
        } else {
          if (ov) ov.style.opacity = "0";
        }
      }

      // ── Click (clip info) ──
      const ray = new THREE.Raycaster(), mp = new THREE.Vector2();
      renderer.domElement.addEventListener("click", (e) => {
        const rect = renderer.domElement.getBoundingClientRect();
        mp.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        mp.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        ray.setFromCamera(mp, cam);
        const h = ray.intersectObjects(meshes);
        const ci = getEl("clip-info");
        if (h.length) {
          const t = h[0].object.userData.t;
          if (sel?.mesh) sel.mesh.position.z = 0;
          sel = t; t.mesh.position.z = 0.002;
          if (ci) ci.style.display = "";
          const cn = getEl("ci-name"); if (cn) { cn.textContent = t.reg; cn.style.color = LEVEL_COLORS[level] || "#ccc"; }
          const cd = getEl("ci-dim"); if (cd) cd.textContent = `${t.w}×${t.h}px`;
        } else {
          if (sel?.mesh) sel.mesh.position.z = 0;
          sel = null;
          if (ci) ci.style.display = "none";
        }
      });

      // ── Double-click zoom ──
      renderer.domElement.addEventListener("dblclick", (e) => {
        const rect = renderer.domElement.getBoundingClientRect();
        mp.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        mp.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
        ray.setFromCamera(mp, cam);
        const h = ray.intersectObjects(meshes);
        if (h.length) {
          const wp = new THREE.Vector3(); h[0].object.getWorldPosition(wp);
          const sp = cam.position.clone(), st = camTarget.clone();
          const np = wp.clone().setZ(wp.z + 2), nt = wp.clone();
          const dur = 500, s0 = performance.now();
          (function step(now) {
            const t = Math.min((now - s0) / dur, 1), ease = 1 - Math.pow(1 - t, 3);
            cam.position.lerpVectors(sp, np, ease); camTarget.lerpVectors(st, nt, ease); cam.lookAt(camTarget);
            if (t < 1) requestAnimationFrame(step);
          })(performance.now());
        }
      });

      // ── Wire up control buttons ──
      const btnPlay = getEl("btn-play");
      if (btnPlay) btnPlay.addEventListener("click", () => { if (on) stop(); else go(level || LEVELS[0]); });
      LEVELS.forEach((l) => {
        const b = getEl(`btn-${l.toLowerCase()}`);
        if (b) b.addEventListener("click", () => { stop(); go(l); });
      });
      // Hide level buttons that don't exist in the data
      ["FOP", "L2", "L3", "L4"].forEach((l) => {
        const b = getEl(`btn-${l.toLowerCase()}`);
        if (b && !LEVELS.includes(l)) b.style.display = "none";
      });
      const btnStripEl = getEl("btn-strip");
      if (btnStripEl) btnStripEl.addEventListener("click", () => { if (mode === "strip") return; mode = "strip"; if (tiles.length) { build(); fit(); } ui(); });
      const btnStackEl = getEl("btn-stack");
      if (btnStackEl) btnStackEl.addEventListener("click", () => { if (mode === "stack") return; mode = "stack"; if (tiles.length) { build(); fit(); } ui(); });
      const btnStop = getEl("btn-stop");
      if (btnStop) btnStop.addEventListener("click", () => { stop(); unload(); level = null; ui(); });
      const btnCont = getEl("btn-continue");
      if (btnCont) btnCont.addEventListener("click", () => { continueToNext(); });

      // ── Render loop ──
      const rvfc = "requestVideoFrameCallback" in HTMLVideoElement.prototype;
      let fc = 0, animId;
      function anim() {
        animId = requestAnimationFrame(anim);
        cam.lookAt(camTarget); fc++;
        if (on) {
          let up = 0;
          for (const t of tiles) {
            if (t.ty !== "v" || !t.vid || t.vid.paused) continue;
            if (rvfc) { if (t.nf && up < MAX_TEX) { t.tex.needsUpdate = true; t.nf = false; up++; } }
            else { if (t.vid.readyState >= 2 && up < MAX_TEX) { t.tex.needsUpdate = true; up++; } }
          }
          if (fc % 30 === 0) sync();
          if (done() && !adv && !waiting) advance();
        }
        if (fc % 6 === 0 && on && !waiting) {
          const tCur = getEl("t-cur");
          if (imageTimerStart && chunk.length > 0 && !chunk.some((t) => t.vid)) {
            // Image-only: show elapsed hold time
            const elapsed = (performance.now() - imageTimerStart) / 1000;
            if (tCur) tCur.textContent = fmt(Math.min(elapsed, IMAGE_HOLD_SECS));
          } else {
            // Video: show video currentTime
            const ref = chunk.find((t) => t.vid && !t.vid.ended && t.vid.readyState >= 2);
            if (ref && tCur) tCur.textContent = fmt(ref.vid.currentTime);
          }
        }
        renderer.render(scene, cam);
      }

      // Expose state
      stateRef.current = { go, stop, LEVELS, getMode: () => mode, getOn: () => on };

      resize(); ui(); anim();

      // ── Cleanup ──
      cleanupFn = () => {
        cancelAnimationFrame(animId);
        window.removeEventListener("resize", resize);
        if (ro) ro.disconnect();
        unload();
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        renderer.domElement.removeEventListener("pointerdown", onPointerDown);
        renderer.domElement.removeEventListener("wheel", onWheel);
        renderer.domElement.removeEventListener("touchstart", onTouchStart);
        renderer.domElement.removeEventListener("touchmove", onTouchMove);
        renderer.domElement.removeEventListener("touchend", onTouchEnd);
        renderer.dispose();
        if (el.contains(renderer.domElement)) el.removeChild(renderer.domElement);
        stateRef.current = null;
      };
    })();

    return () => {
      cancelled = true;
      if (cleanupFn) cleanupFn();
    };
  }, [ribbonItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Styles ──
  const btnBase = {
    border: "1px solid rgba(255,255,255,0.12)", color: "#c9d1d9",
    padding: "8px 18px", borderRadius: 6, cursor: "pointer",
    fontSize: 13, fontWeight: 600, background: "rgba(255,255,255,0.04)",
    transition: "all 0.15s",
  };
  const lvlBtn = { ...btnBase, minWidth: 44, textAlign: "center", fontSize: 12, padding: "7px 14px", fontWeight: 700, letterSpacing: "0.5px" };
  const modeBtn = { ...btnBase, fontSize: 12, padding: "7px 12px", color: "#484f58" };
  const sep = { width: 1, height: 22, background: "rgba(255,255,255,0.06)", flexShrink: 0 };

  return createPortal(
    <div
      ref={wrapperRef}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", top: 0, right: 0, bottom: 0, left: 0,
        zIndex: 99999, background: "rgba(0,0,0,0.96)",
        display: "flex", flexDirection: "column",
        fontFamily: "'Space Grotesk', -apple-system, sans-serif",
      }}
    >
      {/* Close */}
      <button
        onClick={onClose}
        style={{
          position: "absolute", top: 16, right: 20, zIndex: 10,
          background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)",
          borderRadius: 8, padding: "8px 14px", color: "#fff", fontSize: 16, cursor: "pointer",
        }}
      >
        ✕
      </button>

      {/* Title bar — matches Tiled Preview */}
      <div style={{ padding: "12px 0 6px", textAlign: "center", zIndex: 5, flexShrink: 0 }}>
        <div style={{ color: "#fff", fontSize: 16, fontWeight: 600 }}>
          {asset?.originalName || asset?.fileName || "360 Preview"}
        </div>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 4 }}>
          {asset?.lumen8Id && (
            <span style={{ fontSize: 12, fontFamily: "monospace", color: TEAL }}>
              {asset.lumen8Id}
            </span>
          )}
          {asset?.venue && (
            <span style={{ fontSize: 12, color: TEXT_SECONDARY }}>{asset.venue}</span>
          )}
          <span style={{ fontSize: 12, color: TEXT_SECONDARY }}>LED 360</span>
          <span style={{ fontSize: 12, color: TEXT_SECONDARY }}>
            {ribbonItems?.length || 0} clip{ribbonItems?.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* 3D Viewport */}
      <div
        ref={containerRef}
        style={{ flex: 1, position: "relative", overflow: "hidden", minHeight: 0 }}
      >
        {/* Overlay */}
        <div
          data-id="overlay"
          style={{
            position: "absolute", inset: 0, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", zIndex: 15,
            pointerEvents: "none", transition: "opacity 0.5s",
          }}
        >
          <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }}>🏟️</div>
          <div data-id="ov-title" style={{ fontSize: 20, fontWeight: 700, color: "#c9d1d9" }}>
            360 LED Preview
          </div>
          <div data-id="ov-sub" style={{ fontSize: 13, color: "#484f58", marginTop: 4 }}>
            Press Play to start
          </div>
        </div>

        {/* Clip info tooltip */}
        <div
          data-id="clip-info"
          style={{
            position: "absolute", top: 12, right: 12, zIndex: 10,
            background: "rgba(11,14,20,0.92)", border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 8, padding: "10px 14px", fontSize: 12, display: "none",
            minWidth: 170, backdropFilter: "blur(8px)",
          }}
        >
          <div data-id="ci-name" style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }} />
          <div data-id="ci-dim" style={{ color: "#484f58" }} />
        </div>

        {/* Continue overlay */}
        <div data-id="continue-bar" style={{ position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)", zIndex: 12, display: "none" }}>
          <button
            data-id="btn-continue"
            style={{
              background: "rgba(35,134,54,0.3)", border: "1px solid rgba(35,134,54,0.6)",
              color: "#3fb950", padding: "12px 32px", borderRadius: 8, cursor: "pointer",
              fontSize: 15, fontWeight: 700, backdropFilter: "blur(8px)",
            }}
          >
            Continue →
          </button>
        </div>

        {/* Level done badge */}
        <div data-id="level-done" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 11, textAlign: "center", pointerEvents: "none", display: "none" }}>
          <div style={{ fontSize: 32, marginBottom: 4, opacity: 0.6 }}>✓</div>
          <div data-id="ld-text" style={{ fontSize: 14, color: "#8b949e" }} />
        </div>
      </div>

      {/* Info bar */}
      <div
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 20,
          padding: "6px 24px", minHeight: 36, background: "#0d1117",
          borderTop: "1px solid rgba(255,255,255,0.06)", fontSize: 13, flexShrink: 0,
        }}
      >
        <span data-id="ib-level" style={{ fontSize: 15, fontWeight: 800, letterSpacing: 1 }} />
        <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#30363d", flexShrink: 0 }} />
        <span data-id="ib-regions" style={{ color: "#8b949e", fontWeight: 500 }}>—</span>
        <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#30363d", flexShrink: 0 }} />
        <span data-id="ib-progress" style={{ color: "#484f58", fontVariantNumeric: "tabular-nums" }} />
      </div>

      {/* Controls */}
      <div
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          padding: "10px 16px", background: "#161b22",
          borderTop: "1px solid rgba(255,255,255,0.06)", zIndex: 20,
          flexShrink: 0, flexWrap: "wrap",
        }}
      >
        <button data-id="btn-play" style={{ ...btnBase, background: "rgba(35,134,54,0.25)", borderColor: "rgba(35,134,54,0.6)", color: "#3fb950" }}>▶ Play</button>
        <button data-id="btn-stop" style={btnBase}>⏹ Reset</button>
        <div style={sep} />
        <button data-id="btn-fop" style={lvlBtn}>FOP</button>
        <button data-id="btn-l2" style={lvlBtn}>L2</button>
        <button data-id="btn-l3" style={lvlBtn}>L3</button>
        <button data-id="btn-l4" style={lvlBtn}>L4</button>
        <div style={sep} />
        <button data-id="btn-stack" style={modeBtn}>☰ Stack</button>
        <button data-id="btn-strip" style={modeBtn}>━ Strip</button>
        <div style={sep} />
        <div style={{ fontSize: 15, fontWeight: 700, fontVariantNumeric: "tabular-nums", minWidth: 95, textAlign: "center" }}>
          <span data-id="t-cur">0:00</span>{" "}
          <span style={{ color: "#30363d", fontWeight: 400 }}>/ <span data-id="t-dur">0:30</span></span>
        </div>
        <div style={sep} />
        <span style={{ fontSize: 11, color: "#484f58" }}>Pan · Dbl-click zoom · Scroll zoom</span>
      </div>
    </div>,
    document.body
  );
}
